import type { GetContextMenuItems, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise';
import Big from 'big.js';
import { compact, every } from 'lodash-es';

import { OMSView } from '@src/components/OMS/OMSView';
import { modifySalesOrderThunk, resubmitSalesOrderThunk } from '@src/components/OMS/SalesOrder/SalesOrderSlice';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useAdminUtils, useEmbeddedContext, useFeatureFlag, useOrderAccess, useRoleAuth } from '@src/hooks';
import { useOrders, useSubAccounts } from '@src/providers';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { useTradingSettings } from '@src/providers/TradingSettingsContext';
import { useAssertOrderModifiable } from '@src/utils/order';
import {
  ACTION,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  abbreviateId,
  filterByCellValueMenuItem,
  getOrdersForModify,
  getRowNodesToOperateOn,
  getShowJSONContextItem,
  isOrderComplete,
  useDisclosure,
  useDynamicCallback,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  useUserContext,
  type ExecutionReport,
  type FilterableProperty,
  type MixpanelInstance,
  type Order,
  type UseDisclosureReturn,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import React, { useState } from 'react';
import { useDetailsDrawer } from '../../../components/DetailsDrawer/useDetailsDrawer';
import { modifyOrder, resubmitOrder } from '../../../components/OMS/NewOrder/OrderSlice';
import { resubmitRFQ } from '../../../components/OMS/NewRFQ/RFQSlice';
import { openView } from '../../../components/OMS/OMSSlice';
import { mapOrderToCustomerOrder } from '../../../components/OMS/utils';
import { useNavigateToOrderDetails } from '../../../hooks/useNavigateToOrderDetails';
import { CancelSelectedDialog, ForceCancelSelectedDialog } from '../CancelSelectedDialog';
import { useForceCancelCareOrderChildMenuItem } from '../CareOrders/useForceCancelCareOrderChildMenuItem';
import { useCopyCustomerOrderTextMenuItem } from '../CustomerOrders/useCopyCustomerOrderTextMenuItem';
import {
  BulkModifyDialog,
  BulkModifyGroupDialog,
  BulkModifySubAccountDialog,
  BulkModifyWarningDialog,
  BulkResumeDialog,
} from './Dialogs';
import { colIDToFilterBuilderKey } from './useOrderFilter';

export const useOrderMenu = ({
  openClause,
  filterableProperties,
  generateOrderDetailsRoute,
  layoutId,
}: {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty<string>[];
  generateOrderDetailsRoute: GenerateOrderDetailsRoute;
  layoutId: string | undefined;
}): {
  getContextMenuItems: GetContextMenuItems<Order>;
  dialogComponents: JSX.Element[];
} => {
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const mixpanel = useMixpanel();
  const { openDetailsDrawer } = useDetailsDrawer();
  const { pause, resume, cancel, archive, unarchive } = useOrders();
  const { forceCancelOrder } = useAdminUtils();
  const { isAuthorized } = useRoleAuth();
  const { user } = useUserContext();
  const { enableGroups, confirmOrderResume, confirmOrderCancels, enableSalesWorkflow, enableCustomerBlotterColumns } =
    useTradingSettings();
  const { enableArchiveOrders } = useFeatureFlag();
  const { subAccountsEnabled } = useSubAccounts();
  const checkOrderAccess = useOrderAccess();
  const isOrderModifiable = useAssertOrderModifiable();
  const { getForceCancelCareOrderChildOrderItem, dialog: forceCancelCareOrderChildDialog } =
    useForceCancelCareOrderChildMenuItem();

  const resumeDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const forceCancelDialog = useDisclosure();
  const modifySubAccountDialog = useDisclosure();
  const modifyGroupDialog = useDisclosure();
  const bulkModifyDialog = useDisclosure();
  const modifyWarningDialog = useDisclosure();
  const dispatch = useAppStateDispatch();

  const onResume = useDynamicCallback((orders: Order[]) => {
    if (confirmOrderResume) {
      resumeDialog.open();
    } else {
      orders.forEach(order => {
        mixpanel.track(MixpanelEvent.ResumeOrder, { [MixpanelEventProperty.ID]: order.OrderID });
        resume(order.OrderID);
      });
    }
  });

  const onCancel = useDynamicCallback((orders: Order[]) => {
    if (confirmOrderCancels) {
      cancelDialog.open();
    } else {
      orders.forEach(order => {
        mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
        cancel(order.OrderID);
      });
    }
  });

  const cancelItem = useDynamicCallback((orders: Order[]) => cancelOrderMenuItem({ orders, onCancel }));
  const forceCancelItem = useDynamicCallback((orders: Order[]) =>
    forceCancelOrderTalosMenuItem({ orders, forceCancelDialog, mixpanel })
  );
  const resumeItem = useDynamicCallback((orders: Order[]) => resumeOrderMenuItem({ orders, onResume, mixpanel }));
  const modifyWithNewOrderForm = (order: Order | ExecutionReport) => {
    dispatch(modifyOrder(order));
    dispatch(openView(OMSView.NewOrderForm));
  };
  const onArchive = useDynamicCallback((orders: Order[]) => orders.forEach(order => archive(order.OrderID)));
  const archiveItem = useDynamicCallback((orders: Order[]) => archiveOrderMenuItem({ orders, onArchive, mixpanel }));
  const onUnarchive = useDynamicCallback((orders: Order[]) => orders.forEach(order => unarchive(order.OrderID)));
  const unarchiveItem = useDynamicCallback((orders: Order[]) =>
    unarchiveOrderMenuItem({ orders, onUnarchive, mixpanel })
  );
  const modifyItem = useDynamicCallback((orders: Order[]) =>
    modifyOrderMenuItem({
      orders,
      modifyWarningDialog,
      name: user.Name,
      modify: modifyWithNewOrderForm,
      mixpanel,
      isOrderModifiable,
    })
  );
  const modifyCustomerItem = useDynamicCallback((orders: Order[]) =>
    modifyCustomerOrderMenuItem({
      orders,
      modifyWarningDialog,
      name: user.Name,
      modify: order => {
        const customerOrder = mapOrderToCustomerOrder(order);
        dispatch(modifySalesOrderThunk({ order: customerOrder, principalOrderID: order.OrderID }));
        dispatch(openView(OMSView.SalesOrder));
      },
      mixpanel,
      disabled: !enableSalesWorkflow,
    })
  );
  const modifySubaccountItem = useDynamicCallback((orders: Order[]) =>
    modifySubAccountMenuItem({
      orders,
      modifySubAccountDialog,
      subAccountsEnabled,
      mixpanel,
    })
  );
  const modifyGroupItem = useDynamicCallback((orders: Order[]) =>
    modifyGroupMenuItem({ orders, modifyGroupDialog, enableGroups, mixpanel })
  );
  const { isEmbedded } = useEmbeddedContext();
  const onOpenDetails = useNavigateToOrderDetails(generateOrderDetailsRoute, layoutId);
  const openDetailsItem = useDynamicCallback((orders: Order[]) =>
    openDetailsMenuItem({ orders, onOpenDetails, isEmbedded })
  );
  const openAnalyticsItem = useDynamicCallback((orders: Order[]) =>
    openAnalyticsMenuItem({ orders, openDetailsDrawer, mixpanel })
  );
  const pauseMenuItem = useDynamicCallback((orders: Order[]) =>
    pauseOrderMenuItem({ orders, onPause: pause, mixpanel })
  );
  const resubmitMenuItem = useDynamicCallback((orders: Order[]) =>
    resubmitOrderMenuItem({
      orders,
      resubmitOrder: order => {
        if (order.ParentOrderID) {
          const customerOrder = mapOrderToCustomerOrder(order);
          dispatch(resubmitSalesOrderThunk(customerOrder));
          dispatch(openView(OMSView.SalesOrder));
        } else {
          dispatch(resubmitOrder({ order }));
          dispatch(openView(OMSView.NewOrderForm));
        }
      },
      resubmitRFQ: order => {
        dispatch(openView(OMSView.RFQForm));
        dispatch(resubmitRFQ(order));
      },
      mixpanel,
    })
  );
  const resubmitRemainingMenuItem = useDynamicCallback((orders: Order[]) => {
    const resubmitWithNewOrderForm = (order: Order, useRemaining: boolean) => {
      dispatch(resubmitOrder({ order, useRemaining }));
      dispatch(openView(OMSView.NewOrderForm));
    };

    return resubmitRemainingOrdersMenuItem({
      orders,
      openResubmitOrder: resubmitWithNewOrderForm,
      mixpanel,
    });
  });

  const modifyItems = useDynamicCallback((orders: Order[]) =>
    modifyOrderMenuItems({ orders, mixpanel, name: user.Name, bulkModifyDialog, modifyWarningDialog })
  );

  const getMenuItems = useDynamicCallback((orders: Order[]) => {
    let menuItems: (MenuItemDef | null)[] = [openAnalyticsItem(orders)];

    // We only show customer order related menu items if all selected orders are customer orders
    const selectionIncludesCustomerOrders = orders.every(o => !!o.ParentOrderID);

    menuItems.push(openDetailsItem(orders));

    // Only add the Customer Order Execution Copy Details menu item if customer columns are enabled
    if (enableCustomerBlotterColumns) {
      menuItems.push(copyCustomerOrderExecutionTextMenuItem(orders.at(0)));
    }

    const allowedToModifyOrders = every(orders, order => checkOrderAccess(order));
    if (allowedToModifyOrders) {
      const modifyOrderActions = compact([
        // The items without a permission check opens a dialog where we check the permission
        isAuthorized(ACTION.PAUSE_ORDER) && pauseMenuItem(orders),
        cancelItem(orders),
        isAuthorized(ACTION.FORCE_CANCEL_ORDER) && forceCancelItem(orders),
        getForceCancelCareOrderChildOrderItem(orders),
        isAuthorized(ACTION.SUBMIT_ORDER) && modifyItem(orders),
        isAuthorized(ACTION.VIEW_SALES_ORDER_FORM) && modifyCustomerItem(orders),
        modifyItems(orders),
        isAuthorized(ACTION.RESUME_ORDER) && resumeItem(orders),
        isAuthorized(ACTION.SUBMIT_ORDER) && modifySubaccountItem(orders),
        isAuthorized(ACTION.SUBMIT_ORDER) && modifyGroupItem(orders),
        isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && archiveItem(orders),
        isAuthorized(ACTION.ARCHIVE_ORDER) && enableArchiveOrders && unarchiveItem(orders),
      ]);
      menuItems.push(...modifyOrderActions);
      if (isAuthorized(ACTION.SUBMIT_ORDER)) {
        menuItems.push(resubmitMenuItem(orders));
        menuItems.push(resubmitRemainingMenuItem(orders));
      }
    }

    if (!selectionIncludesCustomerOrders) {
      menuItems = menuItems.filter(item => item?.name !== 'Modify Customer Order');
    }

    return menuItems;
  });

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const copyCustomerOrderExecutionTextMenuItem = useCopyCustomerOrderTextMenuItem();
  const { jsonModal, handleClickJson } = useJsonModal();

  const getContextMenuItems: GetContextMenuItems<Order> = useDynamicCallback((params: GetContextMenuItemsParams) => {
    const newSelectedOrders = getRowNodesToOperateOn(params).map(node => node.data);
    setSelectedOrders(newSelectedOrders);
    let menuItems: MenuItemDef[] = [];

    if (filterableProperties != null && openClause != null) {
      menuItems = menuItems.concat(
        filterByCellValueMenuItem({
          params,
          filterableProperties,
          openClause,
          colIDToFilterBuilderKey,
          mixpanel,
        })
      );
    }

    menuItems = menuItems.concat(compact(getMenuItems(newSelectedOrders)));

    if (params.node?.data) {
      menuItems.push(getShowJSONContextItem({ params, handleClickJson }));
    }

    return [...menuItems, ...getDefaultContextMenuItems(params)];
  });

  return {
    getContextMenuItems,
    dialogComponents: [
      <BulkResumeDialog
        key="resume"
        selectedOrders={resumeItem(selectedOrders)?.ordersForResume}
        resumeDialog={resumeDialog}
      />,
      <CancelSelectedDialog
        key="cancel"
        selectedItems={cancelItem(selectedOrders)?.ordersForCancel}
        cancelSelectedDialog={cancelDialog}
        onConfirm={orders =>
          orders.forEach(order => {
            mixpanel.track(MixpanelEvent.CancelOrder, { [MixpanelEventProperty.ID]: order.OrderID });
            cancel(order.OrderID);
          })
        }
      />,
      <ForceCancelSelectedDialog
        key="force-cancel"
        selectedItems={selectedOrders}
        forceCancelSelectedDialog={forceCancelDialog}
        onConfirm={orders => orders.forEach(order => forceCancelOrder(order.OrderID))}
      />,
      <BulkModifyWarningDialog
        key="modify-warn"
        {...modifyWarningDialog}
        onConfirm={() => bulkModifyDialog.open()}
        selectedOrders={getOrdersForModify(selectedOrders)}
      />,
      <BulkModifySubAccountDialog
        key="modify-subaccount"
        selectedOrders={modifySubaccountItem(selectedOrders)?.ordersForSubAccountModify}
        {...modifySubAccountDialog}
      />,
      <BulkModifyGroupDialog
        key="modify-group"
        selectedOrders={modifyGroupItem(selectedOrders)?.ordersForGroupModify}
        {...modifyGroupDialog}
      />,
      <BulkModifyDialog
        key="bulk-modify"
        selectedOrders={getOrdersForModify(selectedOrders)}
        closeOnClickOutside={false}
        {...bulkModifyDialog}
      />,
      <React.Fragment key="force-cancel-care-order-child-order">{forceCancelCareOrderChildDialog}</React.Fragment>,
      <React.Fragment key="show-json-modal">{jsonModal}</React.Fragment>,
    ],
  };
};

interface OrderMenuItemProps {
  orders: Order[];
  mixpanel: MixpanelInstance;
}

const pauseOrderMenuItem = ({ orders, onPause, mixpanel }: OrderMenuItemProps & { onPause: (id: string) => any }) => {
  if (!orders) {
    return null;
  }

  const ordersForPause = orders.filter(order => order.isPausable);
  if (ordersForPause.length === 0) {
    return null;
  }

  if (ordersForPause.length === 1) {
    const order = ordersForPause[0];
    return {
      name: `Pause Order (#<b>${abbreviateId(order.OrderID)}</b>)`,
      action: () => {
        mixpanel.track(MixpanelEvent.PauseOrder);
        onPause(order.OrderID);
      },
      icon: `<i class="ag-icon ${IconName.Pause}"/>`,
    };
  }
  return {
    name: `Pause ${ordersForPause.length} Orders`,
    action: () => {
      mixpanel.track(MixpanelEvent.PauseOrder);
      ordersForPause.forEach(order => onPause(order.OrderID));
    },
    icon: `<i class="ag-icon ${IconName.Pause}"/>`,
  };
};

const cancelOrderMenuItem = ({ orders, onCancel }: { orders: Order[]; onCancel: (orders: Order[]) => void }) => {
  const ordersForCancel = orders.filter(order => !isOrderComplete(order.OrdStatus));
  if (ordersForCancel.length === 0) {
    return null;
  }

  const name = ordersForCancel.length === 1 ? 'Cancel Order' : `Cancel ${ordersForCancel.length} Orders`;
  return {
    name,
    action: () => onCancel(ordersForCancel),
    icon: `<i class="ag-icon ${IconName.Trash}"/>`,
    ordersForCancel,
  };
};
const forceCancelOrderTalosMenuItem = ({
  orders,
  forceCancelDialog,
}: OrderMenuItemProps & {
  forceCancelDialog: UseDisclosureReturn;
}) => {
  if (orders.length === 0) {
    return null;
  }

  const name = orders.length === 1 ? 'Force Cancel Order (Talos)' : `Force Cancel ${orders.length} Orders (Talos)`;
  return {
    name,
    action: () => forceCancelDialog.open(),
    icon: `<i class="ag-icon ${IconName.Trash}"/>`,
  };
};

const modifyOrderMenuItem = ({
  orders,
  modifyWarningDialog,
  name,
  modify,
  mixpanel,
  isOrderModifiable,
}: OrderMenuItemProps & {
  modifyWarningDialog: UseDisclosureReturn;
  name: string;
  modify: (order: Order) => any;
  isOrderModifiable: (order: Order) => boolean;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length !== 1) {
    return null;
  }

  return {
    name: 'Modify Order',
    action: () => {
      mixpanel.track(MixpanelEvent.ModifyOrder);
      const selectedOrder = ordersForModify[0];
      selectedOrder.User === name ? modify(selectedOrder) : modifyWarningDialog.open();
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    // if parentOrderId is set it means it's a customer order, and we don't support modifying customer hedge orders atm
    disabled: !isOrderModifiable(ordersForModify[0]),
  };
};

const modifyCustomerOrderMenuItem = ({
  orders,
  modifyWarningDialog,
  name,
  modify,
  mixpanel,
  disabled,
}: OrderMenuItemProps & {
  modifyWarningDialog: UseDisclosureReturn;
  name: string;
  modify: (order: Order) => any;
  disabled?: boolean;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length !== 1) {
    return null;
  }

  return {
    name: 'Modify Customer Order',
    action: () => {
      mixpanel.track(MixpanelEvent.ModifySalesOrder);
      const selectedOrder = ordersForModify[0];
      modify(selectedOrder);
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    disabled: !ordersForModify[0].isCustomerOrderModifiable || disabled,
  };
};

const modifyOrderMenuItems = ({
  orders,
  mixpanel,
  name,
  bulkModifyDialog,
  modifyWarningDialog,
}: OrderMenuItemProps & {
  name: string;
  bulkModifyDialog: UseDisclosureReturn;
  modifyWarningDialog: UseDisclosureReturn;
}) => {
  const ordersForModify = getOrdersForModify(orders);
  if (ordersForModify.length < 2) {
    return null;
  }

  return {
    name: `Modify End Time`,
    action: () => {
      mixpanel.track(MixpanelEvent.ModifyOrders);
      ordersForModify.some(order => order.User !== name) ? modifyWarningDialog.open() : bulkModifyDialog.open();
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
  };
};

interface OpenOrderMenuItemProps {
  orders: Pick<Order, 'OrderID'>[];
  text?: string;
  onOpenDetails: (orderID: string) => void;
  isEmbedded: boolean;
}

export const openDetailsMenuItem = ({ orders, text, onOpenDetails, isEmbedded }: OpenOrderMenuItemProps) => {
  if (!orders || orders.length > 1 || isEmbedded) {
    return null;
  }
  const order = orders[0];
  return {
    name: text ?? `Open details`,
    action: () => {
      onOpenDetails(order.OrderID);
    },
    icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
  };
};

const openAnalyticsMenuItem = ({
  orders,
  openDetailsDrawer,
  mixpanel,
}: OrderMenuItemProps & { openDetailsDrawer: (props: { entity: Order }) => void }) => {
  if (!orders || orders.length > 1) {
    return null;
  }
  const order = orders[0];
  return {
    name: `Open analytics`,
    action: () => {
      mixpanel.track(MixpanelEvent.OpenOrderAnalytics);
      openDetailsDrawer({ entity: order });
    },
    icon: `<i class="ag-icon ${IconName.ChartLine}"/>`,
  };
};

const resumeOrderMenuItem = ({
  orders,
  onResume,
  mixpanel,
}: OrderMenuItemProps & { onResume: (orders: Order[]) => void }) => {
  if (!orders) {
    return null;
  }

  const ordersForResume = orders.filter(order => !isOrderComplete(order.OrdStatus) && order.isPaused);
  if (ordersForResume.length === 0) {
    return null;
  }

  if (ordersForResume.length === 1) {
    const order = ordersForResume[0];
    return {
      name: `Resume Order (#<b>${abbreviateId(order.OrderID)}</b>)`,
      action: () => {
        onResume([order]);
      },
      icon: `<i class="ag-icon ${IconName.PlayCircle}"/>`,
      ordersForResume,
    };
  }
  return {
    name: `Resume ${ordersForResume.length} Orders`,
    action: () => {
      onResume(ordersForResume);
    },
    icon: `<i class="ag-icon ${IconName.PlayCircle}"/>`,
    ordersForResume,
  };
};

const modifySubAccountMenuItem = ({
  orders,
  modifySubAccountDialog,
  subAccountsEnabled = false,
  mixpanel,
}: OrderMenuItemProps & {
  modifySubAccountDialog: UseDisclosureReturn;
  subAccountsEnabled: boolean;
}) => {
  const ordersForSubAccountModify = orders.filter(
    order => isOrderComplete(order.OrdStatus) && (order.Allocation != null || subAccountsEnabled)
  );
  if (ordersForSubAccountModify.length === 0) {
    return null;
  }
  if (ordersForSubAccountModify.length === 1) {
    const order = ordersForSubAccountModify[0];
    const name = order.Allocation != null ? 'Modify Sub Account(s)' : 'Modify Sub Account';
    return {
      name,
      action: () => {
        modifySubAccountDialog.open();
        mixpanel.track(MixpanelEvent.ModifySubaccount);
      },
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      ordersForSubAccountModify,
    };
  }

  return {
    name: `Modify Sub Account(s) for ${ordersForSubAccountModify.length} Orders`,
    action: () => {
      modifySubAccountDialog.open();
      mixpanel.track(MixpanelEvent.ModifySubaccount);
    },
    icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    ordersForSubAccountModify,
  };
};

const archiveOrderMenuItem = ({
  orders,
  onArchive,
  mixpanel,
}: OrderMenuItemProps & { onArchive: (orders: Order[]) => void }) => {
  const ordersForArchive = orders.filter(order => order.isArchivable);
  if (ordersForArchive.length === 0) {
    return null;
  }
  const actionName = ordersForArchive.length === 1 ? 'Archive Order' : `Archive (${ordersForArchive.length}) Orders`;
  return {
    name: actionName,
    action: () => {
      mixpanel.track(MixpanelEvent.ArchiveOrder);
      onArchive(ordersForArchive);
    },
    icon: `<i class="ag-icon ${IconName.Archive}"/>`,
  };
};

const unarchiveOrderMenuItem = ({
  orders,
  onUnarchive,
  mixpanel,
}: OrderMenuItemProps & { onUnarchive: (orders: Order[]) => void }) => {
  const ordersForUnarchive = orders.filter(order => order.isUnArchivable);
  if (ordersForUnarchive.length === 0) {
    return null;
  }
  const actionName =
    ordersForUnarchive.length === 1 ? 'Unarchive Order' : `Unarchive (${ordersForUnarchive.length}) Orders`;
  return {
    name: actionName,
    action: () => {
      mixpanel.track(MixpanelEvent.UnarchiveOrder);
      onUnarchive(ordersForUnarchive);
    },
    icon: `<i class="ag-icon ${IconName.Archive}"/>`,
  };
};

const modifyGroupMenuItem = ({
  orders,
  modifyGroupDialog,
  enableGroups,
  mixpanel,
}: OrderMenuItemProps & {
  modifyGroupDialog: UseDisclosureReturn;
  enableGroups: boolean;
}) => {
  const ordersForGroupModify = orders.filter(order => isOrderComplete(order.OrdStatus) && enableGroups);
  if (ordersForGroupModify.length === 0) {
    return null;
  }
  if (ordersForGroupModify.length === 1) {
    return {
      name: 'Modify Group',
      action: () => {
        mixpanel.track(MixpanelEvent.ModifyOrderGroup);
        modifyGroupDialog.open();
      },
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      ordersForGroupModify,
    };
  }
  return null; // no bulk order group modification
};

const resubmitOrderMenuItem = ({
  orders,
  resubmitOrder,
  resubmitRFQ,
  mixpanel,
}: OrderMenuItemProps & {
  resubmitOrder: (order: Order, useRemaining: boolean) => any;
  resubmitRFQ: (order: Order) => any;
}) => {
  if (orders.length !== 1) {
    return null;
  }

  // No bulk resubmit
  const order = orders[0];
  if (order.QuoteID) {
    return {
      name: 'Resubmit RFQ',
      action: () => {
        resubmitRFQ(order);
      },
      icon: `<i class="ag-icon ${IconName.Repeat}"/>`,
    };
  }
  return {
    name: 'Resubmit Order',
    action: () => {
      mixpanel.track(MixpanelEvent.OpenResubmitOrder);
      resubmitOrder(order, false);
    },
    icon: `<i class="ag-icon ${IconName.Repeat}"/>`,
  };
};

const resubmitRemainingOrdersMenuItem = ({
  orders,
  openResubmitOrder,
  mixpanel,
}: OrderMenuItemProps & {
  openResubmitOrder: (order: Order, useRemaining: boolean) => any;
}) => {
  if (orders.length !== 1) {
    return null;
  } // Never show when multi selecting
  const ordersForResubmit = orders.filter(order => isOrderComplete(order.OrdStatus));
  if (ordersForResubmit.length !== 1) {
    return null;
  } // No bulk resubmit

  const order = ordersForResubmit[0];
  const { OrderQty, CumQty } = order;
  const remaining = Big(OrderQty).minus(CumQty || '0');
  if (remaining.gt(0)) {
    return {
      name: 'Resubmit Remaining',
      action: () => {
        mixpanel.track(MixpanelEvent.OpenResubmitRemaining);
        openResubmitOrder(order, true);
      },
      icon: `<i class="ag-icon ${IconName.RepeatOnce}"/>`,
    };
  }
  return null;
};
